import { useState, useContext } from 'react'
import { createPortal } from 'react-dom'
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined'
import Modal from '../common/Modal'
import Tooltip from '../common/Tooltip'
import { DashboardCardFooterContext } from './DashboardCard'
import { DateRangeControlRenderer } from '../liveReport/reportBuilder2/controls/DateRangeControl'
import isRangeInverted from '../../helpers/compareHelpers/isRangeInverted'
import formatPeriodLabel from '../../helpers/compareHelpers/formatPeriodLabel'
import InlineDetailsContext from '../../StateManagment/InlineDetailsContext'

// AI-CONTEXT: See docs/features/compare-two-periods.md
// Controlled time-icon control for a dashboard/analytics card: pick a primary
// period and, optionally, a compare period. The parent owns persistence —
// dashboard cards write onChange to their DashboardItems doc, Analyse cards
// keep it in transient React state. The value shape here is exactly what
// resolveComparePeriods() consumes.
//
// Uses a pending "draft" edited inside the Modal and committed on Opslaan, so
// an inverted range can be blocked before it reaches Firestore / the data
// layer (a fetch on start>end silently matches nothing).

/**
 * @typedef {object} CardPeriodValue
 * @property {{start: string|null, end: string|null}} [period] - Primary period (ISO dates)
 * @property {{enabled: boolean, start: string|null, end: string|null}} [compare] - Compare period
 */

/** @param {CardPeriodValue|null|undefined} v @returns {Required<CardPeriodValue>} */
const normalize = (v) => ({
    period: { start: v?.period?.start || null, end: v?.period?.end || null },
    compare: {
        enabled: v?.compare?.enabled === true,
        start: v?.compare?.start || null,
        end: v?.compare?.end || null,
    },
})

/** @param {CardPeriodValue|null|undefined} v — has the user set any bound at all? */
const hasActivePeriod = (v) => {
    const n = normalize(v)
    return Boolean(
        n.period.start || n.period.end
        || (n.compare.enabled && (n.compare.start || n.compare.end))
        || n.compare.enabled
    )
}

/**
 * @param {object} props
 * @param {CardPeriodValue|null} [props.value] - Current period + compare setting
 * @param {(value: CardPeriodValue) => void} props.onChange - Commits the edited value on Opslaan
 * @param {boolean} [props.disabled] - Renders a greyed, non-interactive icon (e.g. tenant not loaded)
 * @returns {JSX.Element}
 */
const CardPeriodControl = ({ value = null, onChange, disabled = false }) => {
    // Portal the trigger into the card's bottom bar (alongside the graph-type /
    // colour icons), the same slot DashboardCardOptions uses. Falls back to
    // inline when there's no footer (e.g. outside a card shell).
    const footerEl = useContext(DashboardCardFooterContext)
    // Deep-analysis pages show labelled buttons instead of bare icons (same
    // switch as DashboardCardOptions). AI-CONTEXT: See docs/features/deep-analysis-period.md
    const onDeepAnalysis = useContext(InlineDetailsContext)
    const [modalOpen, setModalOpen] = useState(false)
    const [draft, setDraft] = useState(() => normalize(value))

    const open = () => {
        if (disabled) return
        setDraft(normalize(value))
        setModalOpen(true)
    }

    const setPeriod = (range) =>
        setDraft(prev => ({ ...prev, period: { start: range.start, end: range.end } }))

    const toggleCompare = (enabled) =>
        setDraft(prev => ({ ...prev, compare: { ...prev.compare, enabled } }))

    const setCompareRange = (range) =>
        setDraft(prev => ({ ...prev, compare: { ...prev.compare, start: range.start, end: range.end } }))

    const periodInverted = isRangeInverted(draft.period)
    const compareInverted = draft.compare.enabled && isRangeInverted(draft.compare)
    const invalid = periodInverted || compareInverted

    const confirm = () => {
        if (invalid) return
        onChange(draft)
        setModalOpen(false)
    }

    const active = hasActivePeriod(value)
    const tooltip = active ? 'Periode & vergelijking aanpassen' : 'Periode kiezen of vergelijken'

    const content = (
        <div className='card-period-control-body'>
            <div className='card-period-control-section'>
                <DateRangeControlRenderer
                    value={draft.period}
                    onChange={setPeriod}
                    label='Periode'
                />
                {periodInverted && (
                    <p className='card-period-control-error'>
                        De einddatum moet op of na de startdatum liggen.
                    </p>
                )}
            </div>

            <label className='card-period-control-compare-toggle'>
                <input
                    type='checkbox'
                    checked={draft.compare.enabled}
                    onChange={(e) => toggleCompare(e.target.checked)}
                />
                <span>Vergelijk met een andere periode</span>
            </label>

            {draft.compare.enabled && (
                <div className='card-period-control-section'>
                    <DateRangeControlRenderer
                        value={{ start: draft.compare.start, end: draft.compare.end }}
                        onChange={setCompareRange}
                        label='Vergelijkingsperiode'
                    />
                    {compareInverted && (
                        <p className='card-period-control-error'>
                            De einddatum moet op of na de startdatum liggen.
                        </p>
                    )}
                </div>
            )}
        </div>
    )

    // Wrapper carries the spacing to the neighbouring footer icons — the Tooltip
    // wraps the icon, so the footer's own `> .dashboard-card-options-icon` gap
    // rule wouldn't reach it.
    const n = normalize(value)
    const buttonLabel = active
        ? `${formatPeriodLabel(n.period)}${n.compare.enabled ? ` vs ${formatPeriodLabel(n.compare)}` : ''}`
        : 'Periode'

    const labelledTrigger = (
        <button type='button' className='deep-analysis-ctrl' onClick={open} disabled={disabled}>
            <DateRangeOutlinedIcon />
            <span>{buttonLabel}</span>
        </button>
    )

    const iconTrigger = (
        <span className='card-period-control-slot'>
            {/* maxWidth, geen vaste `top`: de tekst loopt over twee regels en
                lag met een vaste afstand naar boven óver het icoon. Met maxWidth
                meet de tooltip zijn hoogte en staat hij onder of boven het icoon. */}
            <Tooltip content={tooltip} width='15px' maxWidth='200px'>
                <span
                    className={`dashboard-card-options-icon card-period-control-icon${active ? ' card-period-control-icon--active' : ''}${disabled ? ' card-period-control-icon--disabled' : ''}`}
                    onClick={open}
                >
                    <DateRangeOutlinedIcon />
                </span>
            </Tooltip>
        </span>
    )

    const trigger = onDeepAnalysis ? labelledTrigger : iconTrigger

    return (
        <>
            {footerEl ? createPortal(trigger, footerEl) : trigger}
            <Modal
                modalOpen={modalOpen}
                setModalOpen={setModalOpen}
                confirm={confirm}
                confirmDisabled={invalid}
                pageHeader='Periode & vergelijking'
                content={content}
            />
        </>
    )
}

export default CardPeriodControl
